import {DropdownOption, DropdownOptionIconProps} from "atmosfair-ui";
import {AgencyProps} from "../../api/model/agency";
import {CountryAlpha2Props, CountryProps} from "../../components/settings/utils";
import {TravelClass, TravelClassForDropdown} from "../../screens/activity/activity-form/flight";
import {AircraftTypeResponse} from "../../api/model/activity-flight";
import {ACTIVITY_TYPE} from "../../types-enums/activity";
import {i18n} from "i18next";
import {BundlesPersistingOptions} from "../../container/buttons/save-bundles-button/SaveBundlesButton";
import {HotelClassForDropdown, MealPlan, RoomCategory} from "../../screens/activity/activity-form/hotel";
import {ENUMERATION_CHARACTER, generateTranslatedActivityTypeString} from "../string/common";
import {
    AddActivityFromTravelComponentsTableDropdownProps
} from "../../components/journey/journey-overview/journey-day-card-items/JourneyDayCardItems";
import {isDefined} from "../check/type-guards";
import {theme} from "../../styles/theme";
import {ACTIVITY_ICON_MAPPING} from "../getter/activities";
import {BundleType} from "../../container/buttons/common";
import {HotelClass} from "../../api/model/activity-hotel";

import {
    activityDurationExceedsJourneyDurationFromDay,
    activityPersonCountExceedsJourneyPersonCount
} from "../check/common";
import {Luf} from "../../constants/default-values";

export enum WHICH_CAR_BUS_PROPS {
    CAR, BUS, FUEL_TYPE, GENERAL
}

export enum DELETE_ACTIVITIES_DROPDOWN_SELECTION {
    REMOTE_BUNDLE, LOCAL_ACTIVITIES, SELECTED_ACTIVITIES
}

export const convertDeleteBundlesButtonOptionsToDropdownFormat = (
    option: DropdownOption,
    whatToDelete: BundleType,
    hasBundle: boolean,
    hasActivitiesSelected: boolean,
    i18n: i18n
): DropdownOption[] => {
    const {t} = i18n
    let bundleOption: DropdownOption | null = null
    let activitiesOption: DropdownOption | null = null
    if (hasBundle) {
        switch (whatToDelete) {
            case BundleType.journey:
                bundleOption = {
                    label: 'TOOLTIP.JOURNEY.CLEAR_JOURNEY',
                    value: 0,
                    raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES}
                }
                break;
            case BundleType.travelComponents:
                bundleOption = {
                    label: "TOOLTIP.TRAVEL_COMPONENTS.CLEAR_TABLE",
                    value: 0,
                    raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES}
                }
                break;
            default:
                whatToDelete satisfies never;
        }
    }

    if (hasActivitiesSelected) activitiesOption = {
        label: "TOOLTIP.TRAVEL_COMPONENTS.DELETE_SELECTED_ACTIVITIES",
        value: 0,
        raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.SELECTED_ACTIVITIES}
    }

    return [bundleOption, activitiesOption, option].filter(isDefined).map((dropdownOption, index) => {
        const {label, raw} = dropdownOption!!
        return {
            label: t(label),
            value: index,
            raw
        }
    })
}

export const convertLufToDropdownFormat = (options: typeof Luf, i18n: i18n): DropdownOption[] => {
    const {t} = i18n;
    return Object.entries(options).map(([key, value], index) => {
        return {
            label: t(`ACTIVITIES.${value.toUpperCase()}.TITLE`),
            value: index,
            raw: {name: value, id: index, key}
        };
    });
};

export const convertPersistBundlesButtonOptionsToDropdownFormat = (options: typeof BundlesPersistingOptions, i18n: i18n): DropdownOption[] => {
    const {t} = i18n;
    return Object.entries(options).map(([key, value], index) => {
        return {
            label: t(`LABELS.COMMON.BUNDLE_${value.toUpperCase()}`),
            value: index,
            raw: {name: value, id: index, key}
        };
    });
};

export const convertInternationalPhonePrefixesToDropdownFormat = (internationalPhonePrefixes: CountryAlpha2Props): DropdownOption[] => {
    return (Object.keys(internationalPhonePrefixes) as Array<keyof CountryAlpha2Props>).map((key, index) => {
        const name = internationalPhonePrefixes[key]
        return {
            label: name,
            value: index,
            raw: {name, id: index, key: key as string}
        }
    });
}

interface CommonDropdownOptionsProps {
    value: number;
    raw: { activity: any };
    iconLeft: { icon: any; color: `#${string}` };
    iconRight: undefined;
    label: string
}

const addDisabledExplanationToJourneyScreenDropdown = (
    commonDropdownOptionsProps: CommonDropdownOptionsProps,
    dropdownOptions: DropdownOption[],
    activityPersonCountExceedsJourneyMembersCount: boolean,
    activityExceedsJourneyDuration: boolean,
    i18n: i18n
) => {
    const {t} = i18n
    if (activityPersonCountExceedsJourneyMembersCount) {
        dropdownOptions.push({
            ...commonDropdownOptionsProps,
            disabled: true,
            iconLeft: undefined,
            label: `${ENUMERATION_CHARACTER} ${t("SCREENS.JOURNEY.ACTIVITY_PERSON_COUNT_EXCEEDS_JOURNEY_PERSON_COUNT")}`
        })
    }
    if (activityExceedsJourneyDuration) {
        dropdownOptions.push({
            ...commonDropdownOptionsProps,
            disabled: true,
            iconLeft: undefined,
            label: `${ENUMERATION_CHARACTER} ${t("SCREENS.JOURNEY.ACTIVITY_EXCEEDS_JOURNEY_DURATION")}`
        })
    }
}


export const convertAddActivitiesToJourneyToDropdownFormat = (
    activitiesForDropdown: (AddActivityFromTravelComponentsTableDropdownProps | null)[],
    journeyDurationInDays: number,
    journeyPersonCount: number,
    day: number,
    i18n: i18n,
): DropdownOption[] => {
    const {t} = i18n
    const dropdownOptions: DropdownOption[] = []
    activitiesForDropdown.filter(Boolean).forEach((activityForDropdown, index) => {
        let activityExceedsJourneyDuration = false
        let activityPersonCountExceedsJourneyMembersCount = false
        let disableDropdownOption = false
        if (activityForDropdown) {
            const {label, activity} = activityForDropdown
            const {common: {activityType}} = activity
            let iconRight: DropdownOptionIconProps | undefined = undefined
            let labelWithAdditionalInformation = label
            const commonDropdownOptionsProps: CommonDropdownOptionsProps = {
                value: index,
                raw: {activity},
                iconLeft: {icon: ACTIVITY_ICON_MAPPING[activityType], color: theme.colors.black},
                iconRight,
                label
            }
            switch (activityType) {
                case ACTIVITY_TYPE.car_bus:
                    activityExceedsJourneyDuration = Boolean(activityDurationExceedsJourneyDurationFromDay(activity, day, journeyDurationInDays))
                    activityPersonCountExceedsJourneyMembersCount = activityPersonCountExceedsJourneyPersonCount(activity, journeyPersonCount)
                    disableDropdownOption = activityExceedsJourneyDuration || activityPersonCountExceedsJourneyMembersCount
                    dropdownOptions.push({
                        ...commonDropdownOptionsProps,
                        disabled: disableDropdownOption,
                    })
                    disableDropdownOption && addDisabledExplanationToJourneyScreenDropdown(
                        commonDropdownOptionsProps,
                        dropdownOptions,
                        activityPersonCountExceedsJourneyMembersCount,
                        activityExceedsJourneyDuration,
                        i18n
                    )
                    break;
                case ACTIVITY_TYPE.hotel:
                    activityExceedsJourneyDuration = Boolean(activityDurationExceedsJourneyDurationFromDay(activity, day, journeyDurationInDays))
                    activityPersonCountExceedsJourneyMembersCount = activityPersonCountExceedsJourneyPersonCount(activity, journeyPersonCount)
                    disableDropdownOption = activityExceedsJourneyDuration || activityPersonCountExceedsJourneyMembersCount
                    dropdownOptions.push({
                        ...commonDropdownOptionsProps,
                        disabled: disableDropdownOption,
                    })
                    disableDropdownOption && addDisabledExplanationToJourneyScreenDropdown(
                        commonDropdownOptionsProps,
                        dropdownOptions,
                        activityPersonCountExceedsJourneyMembersCount,
                        activityExceedsJourneyDuration,
                        i18n
                    )
                    break;
                case ACTIVITY_TYPE.flight:
                    dropdownOptions.push({...commonDropdownOptionsProps, label: labelWithAdditionalInformation})
                    break;
                default:
                    activityType satisfies never
            }
        }
        activityExceedsJourneyDuration = false
        activityPersonCountExceedsJourneyMembersCount = false
    })
    return dropdownOptions
}

export const convertActivityTypesToDropdownFormat = (activityTypes: ACTIVITY_TYPE[], i18n: i18n,): DropdownOption[] => {
    return Object.values(activityTypes).map((activityType, index) => ({
        label: generateTranslatedActivityTypeString(activityType, i18n),
        value: index,
        iconLeft: {icon: ACTIVITY_ICON_MAPPING[activityType], color: theme.colors.black},
        raw: {activityType, id: index}
    }))
}

export const convertJourneyDurationToDropdownFormat = (journeyDuration: number[], i18n: i18n): DropdownOption[] => {
    const {t} = i18n
    return journeyDuration.map((day, index) => ({
        label: `${t("GENERAL.ADD_TO")} ${t("GENERAL.DAY")} ${day}`,
        value: day,
        raw: {name: day, id: index}
    }))
}

export const convertCarBusEnumToDropdownFormat = (options: Record<string, string>, i18n: i18n, whichCarBusProps: WHICH_CAR_BUS_PROPS): DropdownOption[] => {
    const {t} = i18n
    let label = ""
    switch (whichCarBusProps) {
        case WHICH_CAR_BUS_PROPS.GENERAL:
            label = `ACTIVITIES.CAR_BUS`
            break;
        case WHICH_CAR_BUS_PROPS.CAR:
            label = `ACTIVITIES.CAR_BUS.CAR_CATEGORIES`
            break;
        case WHICH_CAR_BUS_PROPS.BUS:
            label = `ACTIVITIES.CAR_BUS.BUS_CATEGORIES`
            break;
        case WHICH_CAR_BUS_PROPS.FUEL_TYPE:
            label = `ACTIVITIES.CAR_BUS.FUEL_TYPE`
            break;
        default:
            whichCarBusProps satisfies never
    }

    return Object.values(options).map((option, index) => ({
            label: t(`${label}.${option.toUpperCase()}`),
            value: index,
            raw: {name: option, id: index},
            disabled: (`${label}.${option.toUpperCase()}` === "ACTIVITIES.CAR_BUS.CAR_CATEGORIES.MIDIBUS_CAR"),
        })
    )
}

export const convertCountriesToDropdownFormat = (countries: string[]): DropdownOption[] => {
    return countries.map((country, index) => {
        return {
            label: country,
            value: index,
            raw: {name: country, id: index}
        }
    });
}

export const convertAgenciesToDropdownFormat = (agencies: AgencyProps[]): DropdownOption[] => {
    return agencies.map(agency => {
        const {name, id} = agency
        return {
            label: name,
            value: id,
            raw: agency
        }
    })
}

export const convertCountriesFromDropdownFormat = (countries: DropdownOption[] | null): CountryProps | null => {
    if (countries === null) return null
    return countries.map(country => country.raw)[0]
}

export const convertAgenciesFromDropdownFormat = (agencies: DropdownOption[] | null): AgencyProps | null => {
    if (agencies === null) return null
    return agencies.map(agency => agency.raw)[0]
}

export const convertAircraftTypeToDropdownFormat = (aircrafts: AircraftTypeResponse[]): DropdownOption[] => {
    return aircrafts.map(aircraft => {
        const {id, name, iata} = aircraft
        return {
            label: name,
            value: id,
            raw: {name, iata, id}
        }
    });
}

export const convertTravelClassesToDropdownFormat = (travelClasses: TravelClassForDropdown[]): DropdownOption[] => {
    return travelClasses.map(travelClass => {
        const {name, id, value} = travelClass
        return {
            label: name,
            value: id,
            raw: {name, id, value}
        }
    });
}

export const convertTravelClassesFromDropdownFormat = (travelClasses: DropdownOption[] | null): TravelClass | null => {
    if (travelClasses === null) return null
    return travelClasses.map(travelClass => travelClass.raw)[0].value
}

export const convertAircraftTypeFromDropdownFormat = (aircraftTypes: DropdownOption[] | null): AircraftTypeResponse | null => {
    if (aircraftTypes === null) return null
    return aircraftTypes.map(aircraftType => aircraftType.raw)[0]
}

export const convertHotelClassesToDropdownFormat = (hotelClasses: HotelClassForDropdown[]): DropdownOption[] => {
    return hotelClasses.map(hotelClass => {
        const {name, id, value} = hotelClass
        return {
            label: name,
            value: id,
            raw: {name, id, value}
        }
    });
}

export const convertHotelClassesFromDropdownFormat = (hotelClasses: DropdownOption[] | null): HotelClass | null => {
    if (hotelClasses === null) return null
    return hotelClasses.map(hotelClass => hotelClass.raw)[0].value
}

export const convertRoomClassesToDropdownFormat = (roomClasses: RoomCategory[]): DropdownOption[] => {
    return roomClasses.map(roomClass => {
        const {name, id, value} = roomClass
        return {
            disabled: `${name.toUpperCase()}` !== "SCREENS.HOTEL.ROOM_CATEGORY_DS", //TODO("Remove once different emission factors for room category are available")
            label: name,
            value: id,
            raw: {name, id, value}
        }
    });
}

export const convertRoomClassesFromDropdownFormat = (roomClasses: DropdownOption[] | null): RoomCategory | null => {
    if (roomClasses === null) return null
    return roomClasses.map(roomClass => roomClass.raw)[0]
}

export const convertMealClassesToDropdownFormat = (mealClasses: MealPlan[]): DropdownOption[] => {
    return mealClasses.map(mealClass => {
        const {name, id, value} = mealClass
        return {
            label: name,
            value: id,
            raw: {name, id, value}
        }
    });
}

export const convertMealClassesFromDropdownFormat = (mealClasses: DropdownOption[] | null): MealPlan | null => {
    if (mealClasses === null) return null
    return mealClasses.map(mealClass => mealClass.raw)[0]
}